/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Hand off to Figma", description = "Bring a finished ad into Figma as an editable, layered frame using the Hightouch Figma plugin, so your design team can polish the layout in their own tools.", lastUpdatedDate = new Date(1792540800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Hand off to Figma",
        description: "Bring a finished ad into Figma as an editable, layered frame using the Hightouch Figma plugin, so your design team can polish the layout in their own tools.",
        lastUpdatedDate: "Wed Oct 21 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Set up the plugin",
          "depth": 0,
          "id": "set-up-the-plugin"
        }, {
          "value": "Find the plugin",
          "depth": 1,
          "id": "find-the-plugin"
        }, {
          "value": "Connect to Hightouch",
          "depth": 1,
          "id": "connect-to-hightouch"
        }, {
          "value": "Disconnect the plugin",
          "depth": 1,
          "id": "disconnect-the-plugin"
        }, {
          "value": "Import an ad",
          "depth": 0,
          "id": "import-an-ad"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      p: "p",
      em: "em",
      hr: "hr",
      a: "a",
      h2: "h2",
      h3: "h3",
      ol: "ol",
      li: "li",
      code: "code",
      img: "img",
      h4: "h4"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Marketers, creative teams, designers"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsx(_components.td, {
              children: _jsx("ul", {
                children: _jsx("li", {
                  children: "A finished static ad"
                })
              })
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Bring a finished ad into Figma when your design team wants to polish the layout in their own tools. The ad arrives as a fully editable, layered frame — text, imagery, and background as separate layers — so they refine it in Figma instead of rebuilding it by hand."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsxs(_components.p, {
        children: ["Figma handoff is available on static ads. The import runs from the Hightouch plugin inside Figma, not from Hightouch: selecting ", _jsx(_components.strong, {
          children: "Open in Figma"
        }), " in the ", _jsx(_components.a, {
          href: "/ad-studio/refine#favorite-queue-and-export-ads",
          children: "toolbar"
        }), " shows the setup steps rather than exporting a file. Figma export isn't available in every workspace — if selecting the icon shows a prompt instead of the setup steps, it isn't enabled for yours."]
      }), "\n", _jsx(_components.h2, {
        id: "set-up-the-plugin",
        children: "Set up the plugin"
      }), "\n", _jsx(_components.p, {
        children: "Install the plugin and connect it to your Hightouch workspace once. After that, the plugin works in any Figma file you open."
      }), "\n", _jsxs(_components.p, {
        children: ["This is a different connection from ", _jsx(_components.a, {
          href: "/context-hub/connections#connect-figma-designs",
          children: "Figma Designs"
        }), " in Context Hub, which lets Hightouch read your Figma files to import designs as templates."]
      }), "\n", _jsx(_components.h3, {
        id: "find-the-plugin",
        children: "Find the plugin"
      }), "\n", _jsxs(_components.p, {
        children: ["You run every plugin command from the ", _jsx(_components.strong, {
          children: "Tools"
        }), " tab in Figma, so this is also how you get back to the plugin later."]
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["In any Figma design file, select ", _jsx(_components.strong, {
              children: "Tools"
            }), " in the left navigation."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Search for ", _jsx(_components.code, {
              children: "Hightouch"
            }), " and select the ", _jsx(_components.strong, {
              children: "Hightouch"
            }), " plugin. Its details open with a ", _jsx(_components.strong, {
              children: "Run"
            }), " menu that lists the plugin's commands."]
          }), "\n", _jsx(_components.p, {
            children: _jsx(_components.img, {
              src: "ad-studio/figma/as-figma-tools-search.png",
              alt: "Figma Tools tab searching for Hightouch, with the plugin details and Run button open"
            })
          }), "\n"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "If your company's Figma organization requires approval for plugins, ask your Figma admin to approve the Hightouch plugin first."
      }), "\n", _jsxs(_components.p, {
        children: ["You can also search for the plugin from ", _jsx(_components.strong, {
          children: "Actions"
        }), " in the toolbar (", _jsx(_components.code, {
          children: "⌘K"
        }), "), or open it from its ", _jsx(_components.a, {
          href: "https://www.figma.com/community/plugin/1590049317761721632/hightouch",
          children: "Figma Community page"
        }), " in a browser."]
      }), "\n", _jsx(_components.h3, {
        id: "connect-to-hightouch",
        children: "Connect to Hightouch"
      }), "\n", _jsx(_components.p, {
        children: "The plugin connects to Hightouch in one of two ways. Which one you use depends on whether the person working in Figma has their own Hightouch account."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "If the person using the plugin…"
            }), _jsx(_components.th, {
              children: "Connect with"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Is a member of your Hightouch workspace"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#sign-in-with-hightouch",
                children: "Sign in with Hightouch"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Doesn't have a Hightouch account, such as a designer at an agency"
            }), _jsxs(_components.td, {
              children: [_jsx(_components.a, {
                href: "#connect-with-an-api-key",
                children: "An API key"
              }), " from a workspace admin"]
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: "Sign in whenever you can. The key it creates acts as you and only works with the plugin, so the plugin can't do anything your own account can't. An API key you paste in works with the full Hightouch API, so anyone who holds it has the access of the key's user group."
      }), "\n", _jsx(_components.h4, {
        id: "sign-in-with-hightouch",
        children: "Sign in with Hightouch"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["In the Hightouch plugin details, select ", _jsx(_components.strong, {
              children: "Run > Connect to Hightouch"
            }), "."]
          }), "\n", _jsx(_components.p, {
            children: _jsx(_components.img, {
              src: "ad-studio/figma/as-figma-tools-run-connect.png",
              alt: "Hightouch plugin Run menu in the Figma Tools tab with Connect to Hightouch highlighted"
            })
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Select ", _jsx(_components.strong, {
              children: "Sign in with Hightouch"
            }), ". Hightouch opens in your browser, and the plugin shows a one-time code."]
          }), "\n", _jsx(_components.p, {
            children: _jsx(_components.img, {
              src: "ad-studio/figma/as-figma-connect-dialog.png",
              alt: "Connect to Hightouch dialog in the Figma plugin with the Sign in with Hightouch button highlighted"
            })
          }), "\n", _jsx(_components.p, {
            children: _jsx(_components.img, {
              src: "ad-studio/figma/as-figma-one-time-code.png",
              alt: "Finish signing in screen in the Figma plugin showing a one-time code"
            })
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["In the browser, confirm the workspace named on the ", _jsx(_components.strong, {
              children: "Connect the Figma plugin"
            }), " page. To connect a different one, select ", _jsx(_components.strong, {
              children: "Use a different workspace"
            }), "."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Enter the code from the plugin in ", _jsx(_components.strong, {
              children: "Confirmation code"
            }), " and select ", _jsx(_components.strong, {
              children: "Connect plugin"
            }), ". You get one attempt: a wrong code cancels the request, and you start over from step 1 in Figma."]
          }), "\n", _jsx(_components.p, {
            children: _jsx(_components.img, {
              src: "ad-studio/figma/as-figma-confirmation-code.png",
              alt: "Connect the Figma plugin page in Hightouch with the Confirmation code field"
            })
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Return to Figma. The plugin shows ", _jsx(_components.strong, {
              children: "Hightouch is connected"
            }), ". Select ", _jsx(_components.strong, {
              children: "Done"
            }), "."]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Signing in creates an API key named ", _jsx(_components.strong, {
          children: "Figma plugin"
        }), " followed by your email address. You'll find it in ", _jsx(_components.strong, {
          children: "Settings > Workspace > API keys"
        }), "."]
      }), "\n", _jsx(_components.h4, {
        id: "connect-with-an-api-key",
        children: "Connect with an API key"
      }), "\n", _jsx(_components.p, {
        children: "Use an API key when the designer working in Figma doesn't have a Hightouch login. A workspace admin creates the key and shares it with the designer."
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["A workspace admin ", _jsx(_components.a, {
              href: "/developer-tools/api-guide#create-an-api-key",
              children: "creates an API key"
            }), ". For ", _jsx(_components.strong, {
              children: "User group"
            }), ", pick a group with access to Ad Studio."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsx(_components.p, {
            children: "The admin shares the key with the designer through a password manager or another secure channel."
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["In Figma, the designer ", _jsx(_components.a, {
              href: "#find-the-plugin",
              children: "finds the plugin"
            }), " and selects ", _jsx(_components.strong, {
              children: "Run > Connect to Hightouch"
            }), "."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["The designer expands ", _jsx(_components.strong, {
              children: "Advanced: use an API key"
            }), ", pastes the key in ", _jsx(_components.strong, {
              children: "API key"
            }), ", and selects ", _jsx(_components.strong, {
              children: "Save API key"
            }), "."]
          }), "\n", _jsx(_components.p, {
            children: _jsx(_components.img, {
              src: "ad-studio/figma/as-figma-connect-api-key.png",
              alt: "Connect to Hightouch dialog with Advanced: use an API key expanded to show the API key field"
            })
          }), "\n"]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "disconnect-the-plugin",
        children: "Disconnect the plugin"
      }), "\n", _jsxs(_components.p, {
        children: ["Selecting ", _jsx(_components.strong, {
          children: "Disconnect"
        }), " in the plugin removes the key from that copy of the plugin only. To cut off access everywhere, a workspace admin ", _jsx(_components.a, {
          href: "/developer-tools/api-guide#revoke-an-api-key",
          children: "deletes the key"
        }), ": on the ", _jsx(_components.strong, {
          children: "API keys"
        }), " tab, select the key and then select ", _jsx(_components.strong, {
          children: "Delete selected"
        }), ". Delete a shared key when the designer no longer needs it, such as when an agency engagement ends."]
      }), "\n", _jsx(_components.h2, {
        id: "import-an-ad",
        children: "Import an ad"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["In ", _jsx(_components.strong, {
              children: "Tools"
            }), ", select the Hightouch plugin, then select ", _jsx(_components.strong, {
              children: "Run > Import ads from Hightouch…"
            }), "."]
          }), "\n", _jsx(_components.p, {
            children: _jsx(_components.img, {
              src: "ad-studio/figma/as-figma-tools-run-import.png",
              alt: "Hightouch plugin Run menu in the Figma Tools tab with Import ads from Hightouch highlighted"
            })
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsx(_components.p, {
            children: "Search for ads by the name they have in Hightouch, and select the ones you want."
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["To bring in only some sizes of an ad, select ", _jsx(_components.strong, {
              children: "All sizes"
            }), " next to it, choose the sizes, and select ", _jsx(_components.strong, {
              children: "Use selected sizes"
            }), ". Otherwise, every size is imported."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Select ", _jsx(_components.strong, {
              children: "Import"
            }), ", or ", _jsx(_components.strong, {
              children: "Import all"
            }), " to bring in every ad in the list."]
          }), "\n", _jsx(_components.p, {
            children: _jsx(_components.img, {
              src: "ad-studio/figma/as-figma-import-ads-picker.png",
              alt: "Hightouch plugin ad picker with one ad selected and the Import, Import all, and All sizes buttons"
            })
          }), "\n"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Each ad lands on the canvas as a layered frame named after the ad, with text, imagery, and background as separate layers you can edit directly."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "ad-studio/figma/as-figma-imported-ad-frame.png",
          alt: "An imported ad on the Figma canvas with its call-to-action button selected as a separate, editable layer"
        })
      })]
    });
  }
}
export default MDXContent;
